import { useMemo, useState } from 'react';
import './CustomerAnalyticsSection.css';

function toCsv(rows) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(esc).join(',')).join('\r\n');
}

function downloadCsv(filename, text) {
  // BOM para que Excel respete los acentos
  const blob = new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Segmentos RFM: etiqueta, color y criterio (quién entra — en el tooltip de info).
const SEGMENTS = {
  champions: { label: 'Campeones', color: '#16a34a', tip: 'Compraron 3 o más veces y su última compra fue hace 45 días o menos. Tus mejores clientes: frecuentes y activos.' },
  loyal: { label: 'Leales', color: '#0d9488', tip: 'Compraron 2 o más veces y su última compra fue hace 90 días o menos. Clientes que ya volvieron al menos una vez.' },
  new: { label: 'Nuevos', color: '#1B3DFB', tip: 'Hicieron su primera (y única) compra hace 45 días o menos. Recién llegados: clave lograr que repitan.' },
  occasional: { label: 'Ocasionales', color: '#64748b', tip: 'Una sola compra, hace entre 45 y 180 días. Todavía recuperables si se los reactiva.' },
  at_risk: { label: 'En riesgo', color: '#d97706', tip: 'Compraron 2 o más veces pero no vuelven hace entre 90 y 180 días. Se están enfriando: reactivar ya.' },
  dormant: { label: 'Dormidos', color: '#ea580c', tip: 'Sin comprar hace entre 180 y 365 días. Inactivos, principales candidatos a una campaña de reactivación.' },
  lost: { label: 'Perdidos', color: '#dc2626', tip: 'Sin comprar hace más de 365 días.' },
};
const SEGMENT_ORDER = ['champions', 'loyal', 'new', 'occasional', 'at_risk', 'dormant', 'lost'];

const MONTH_LABEL = (m) => {
  const [y, mo] = m.split('-');
  return `${['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][+mo]} ${y.slice(2)}`;
};

function makeFmt(currency) {
  const money = new Intl.NumberFormat('es-UY', { style: 'currency', currency, maximumFractionDigits: 0 });
  const num = new Intl.NumberFormat('es-UY');
  return {
    money: (v) => (v == null ? '—' : money.format(v)),
    num: (v) => (v == null ? '—' : num.format(v)),
    pct: (v) => (v == null ? '—' : `${(v * 100).toLocaleString('es-UY', { maximumFractionDigits: 1 })}%`),
  };
}

function InfoTip({ text }) {
  return (
    <span className="cust-info" tabIndex={0} role="img" aria-label={text}>
      i<span className="cust-tip">{text}</span>
    </span>
  );
}

function Strip({ h, f }) {
  const items = [
    { k: 'Clientes', v: f.num(h.customers) },
    { k: 'Pedidos / cliente', v: h.orders_per_customer?.toLocaleString('es-UY', { maximumFractionDigits: 2 }) ?? '—' },
    { k: 'Recompra', v: f.pct(h.repeat_rate) },
    { k: 'Revenue recurrente', v: f.pct(h.returning_revenue_pct) },
    { k: 'Ticket promedio', v: f.money(h.aov) },
    { k: 'Revenue total', v: f.money(h.revenue) },
  ];
  return (
    <div className="cust-strip">
      {items.map((it) => (
        <div className="cust-stat" key={it.k}>
          <div className="k">{it.k}</div>
          <div className="v">{it.v}</div>
        </div>
      ))}
    </div>
  );
}

function RfmSegments({ rfm, f, exportFetcher }) {
  const total = rfm.reduce((s, x) => s + x.customers, 0) || 1;
  const byKey = Object.fromEntries(rfm.map((x) => [x.key, x]));
  const [busy, setBusy] = useState(null);
  const [errKey, setErrKey] = useState(null);

  async function onExport(key) {
    if (!exportFetcher) return;
    setBusy(key);
    setErrKey(null);
    try {
      const res = await exportFetcher(key);
      const rows = res?.customers ?? [];
      const header = ['Email', 'Nombre', 'Compras', 'Gastado', 'Primera compra', 'Última compra', 'Departamento'];
      const body = rows.map((c) => [c.email, c.name, c.orders, c.spent, c.first_order, c.last_order, c.region]);
      const brandSlug = (res?.brand?.name ?? 'marca').toLowerCase().replace(/\s+/g, '-');
      const today = new Date().toISOString().slice(0, 10);
      downloadCsv(`${brandSlug}_${key}_${today}.csv`, toCsv([header, ...body]));
    } catch {
      setErrKey(key);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="cust-panel">
      <div className="cust-ph">Segmentos de clientes (RFM)</div>
      <div className="cust-rfm-bar">
        {SEGMENT_ORDER.map((key) => {
          const s = byKey[key];
          if (!s || !s.customers) return null;
          const w = (s.customers / total) * 100;
          return (
            <div
              key={key}
              className="cust-rfm-seg"
              style={{ width: `${w}%`, background: SEGMENTS[key].color }}
              title={`${SEGMENTS[key].label}: ${f.num(s.customers)} (${f.pct(s.pct)})`}
            />
          );
        })}
      </div>
      <div className="cust-rfm-grid">
        {SEGMENT_ORDER.map((key) => {
          const s = byKey[key];
          if (!s) return null;
          const meta = SEGMENTS[key];
          return (
            <div className="cust-rfm-card" key={key}>
              <div className="cust-rfm-head">
                <span className="dot" style={{ background: meta.color }} />
                <span className="name">{meta.label}</span>
                <InfoTip text={meta.tip} />
                <span className="pct">{f.pct(s.pct)}</span>
              </div>
              <div className="cust-rfm-num">{f.num(s.customers)} <span>clientes</span></div>
              <div className="cust-rfm-meta">
                <span>{f.money(s.revenue)}</span>
                <span className="ltv">LTV {f.money(s.avg_ltv)}</span>
              </div>
              {exportFetcher && s.customers > 0 && (
                <button
                  type="button"
                  className="cust-exp"
                  disabled={busy === key}
                  onClick={() => onExport(key)}
                >
                  {busy === key ? 'Exportando…' : `Exportar ${f.num(s.customers)}`}
                </button>
              )}
              {errKey === key && <div className="cust-exp-err">No se pudo exportar. Probá de nuevo.</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RetentionHeatmap({ cohorts, avg, f, maxCols = 7 }) {
  const recent = cohorts.slice(-12);
  const cols = Array.from({ length: maxCols }, (_, i) => i);
  const cell = (v) => {
    if (v == null) return { background: 'transparent', color: '#cbd5e1' };
    const a = Math.min(1, Math.sqrt(v)); // sqrt para resaltar valores bajos
    return { background: `rgba(27,61,251,${0.08 + a * 0.8})`, color: a > 0.5 ? '#fff' : '#1e293b' };
  };
  return (
    <div className="cust-panel">
      <div className="cust-ph">Retención por cohorte de adquisición</div>
      <p className="cust-sub">% de clientes de cada cohorte (mes de 1ª compra) que volvió a comprar en los meses siguientes.</p>
      <div className="cust-tablewrap">
        <table className="cust-heat">
          <thead>
            <tr>
              <th className="lbl">Cohorte</th>
              <th className="n">Clientes</th>
              {cols.map((c) => <th key={c}>{c === 0 ? 'M0' : `M+${c}`}</th>)}
            </tr>
          </thead>
          <tbody>
            {recent.map((row) => (
              <tr key={row.cohort}>
                <td className="lbl">{MONTH_LABEL(row.cohort)}</td>
                <td className="n">{f.num(row.size)}</td>
                {cols.map((c) => (
                  <td key={c} style={cell(row.retention[c])}>
                    {row.retention[c] == null ? '' : f.pct(row.retention[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="lbl">Promedio</td>
              <td className="n" />
              {cols.map((c) => (
                <td key={c} className="avg">{avg[c] == null ? '' : f.pct(avg[c])}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Curva de LTV acumulado promedio por mes desde la 1ª compra. Para cada offset
// promedia (ponderado por tamaño) solo las cohortes que YA alcanzaron ese mes,
// así el valor no queda subestimado por cohortes jóvenes.
function avgLtvCurve(cohorts, asOf, maxOffset = 12) {
  const [ay, am] = asOf.split('-').map(Number);
  const curve = [];
  for (let k = 0; k <= maxOffset; k += 1) {
    let num = 0;
    let den = 0;
    for (const c of cohorts) {
      const [cy, cm] = c.cohort.split('-').map(Number);
      const age = (ay - cy) * 12 + (am - cm);
      if (age >= k && c.cumulative_ltv[k] != null) {
        num += c.cumulative_ltv[k] * c.size;
        den += c.size;
      }
    }
    curve.push(den ? num / den : null);
  }
  return curve;
}

function LtvCurve({ cohorts, asOf, f }) {
  const curve = useMemo(() => avgLtvCurve(cohorts, asOf), [cohorts, asOf]);
  const maxv = Math.max(1, ...curve.filter((v) => v != null));
  const tiles = [[0, '1ª compra'], [3, 'A 3 meses'], [6, 'A 6 meses'], [12, 'A 12 meses']];
  const uplift = curve[0] && curve[12] != null ? curve[12] / curve[0] : null;
  return (
    <div className="cust-panel">
      <div className="cust-ph">LTV — cuánto vale un cliente con el tiempo</div>
      <p className="cust-sub">
        Valor promedio acumulado por cliente desde su 1ª compra. Cada mes promedia las cohortes
        que ya alcanzaron esa antigüedad.{uplift ? ` A 12 meses el cliente vale ${uplift.toLocaleString('es-UY', { maximumFractionDigits: 1 })}× su primera compra.` : ''}
      </p>
      <div className="cust-ltv-tiles">
        {tiles.map(([k, lbl]) => (
          <div className="cust-ltv-tile" key={k}>
            <div className="k">{lbl}</div>
            <div className="v">{f.money(curve[k])}</div>
          </div>
        ))}
      </div>
      <div className="cust-ltv-bars">
        {curve.map((v, k) => (
          <div className="col" key={k} title={`Mes ${k}: ${f.money(v)}`}>
            <div className="bar" style={{ height: `${v != null ? (v / maxv) * 100 : 0}%` }} />
            <div className="lb">{k === 0 ? 'M0' : `M${k}`}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewVsReturning({ rows, f }) {
  const recent = rows.slice(-12);
  const max = Math.max(1, ...recent.map((r) => r.new_revenue + r.returning_revenue));
  const totNew = recent.reduce((s, r) => s + r.new_revenue, 0);
  const totRet = recent.reduce((s, r) => s + r.returning_revenue, 0);
  const [hover, setHover] = useState(null);
  const sel = hover != null ? recent[hover] : null;
  const rNew = sel ? sel.new_revenue : totNew;
  const rRet = sel ? sel.returning_revenue : totRet;
  const rTot = rNew + rRet || 1;
  return (
    <div className="cust-panel">
      <div className="cust-ph">Revenue: nuevos vs recurrentes</div>
      <div className="cust-nvr-read">
        <span className="when">{sel ? MONTH_LABEL(sel.month) : 'Período completo'}</span>
        <span className="val"><i style={{ background: '#1B3DFB' }} /> Nuevos <b>{f.money(rNew)}</b> · {f.pct(rNew / rTot)}</span>
        <span className="val"><i style={{ background: '#93c5fd' }} /> Recurrentes <b>{f.money(rRet)}</b> · {f.pct(rRet / rTot)}</span>
      </div>
      <div className="cust-nvr">
        {recent.map((r, idx) => {
          const tot = r.new_revenue + r.returning_revenue;
          return (
            <div
              className={`cust-nvr-col${hover === idx ? ' hot' : ''}`}
              key={r.month}
              onMouseEnter={() => setHover(idx)}
              onMouseLeave={() => setHover(null)}
            >
              <div className="bars" style={{ height: `${(tot / max) * 100}%` }}>
                <div className="ret" style={{ height: `${tot ? (r.returning_revenue / tot) * 100 : 0}%` }} />
                <div className="new" style={{ height: `${tot ? (r.new_revenue / tot) * 100 : 100}%` }} />
              </div>
              <div className="lbl">{MONTH_LABEL(r.month)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Sección "Clientes": analítica de clientes order-level — headline, segmentos
 * RFM (con export por segmento), retención por cohorte, revenue nuevos vs
 * recurrentes y curva de LTV. Estética del reporte (Sora/Manrope), scope `.cust`.
 */
export default function CustomerAnalyticsSection({ data, isLoading, isError, exportFetcher }) {
  const f = useMemo(() => makeFmt(data?.currency || 'UYU'), [data?.currency]);

  if (isLoading && !data) return <p className="py-12 text-center text-sm text-gray-500">Cargando clientes…</p>;
  if (isError || !data) return <p className="py-12 text-center text-sm text-red-600">No se pudo cargar la analítica de clientes.</p>;
  if (!data.has_data) return <p className="py-12 text-center text-sm text-gray-500">Todavía no hay órdenes para analizar.</p>;

  return (
    <div className="cust">
      <Strip h={data.headline} f={f} />
      <RfmSegments rfm={data.rfm} f={f} exportFetcher={exportFetcher} />
      <RetentionHeatmap cohorts={data.cohorts.cohorts} avg={data.cohorts.avg_retention} f={f} />
      <NewVsReturning rows={data.new_vs_returning} f={f} />
      <LtvCurve cohorts={data.ltv.cohorts} asOf={data.as_of} f={f} />
    </div>
  );
}
